<template>
  <div ref="containerRef" class="bea-container">
    <div v-if="loading.visible" class="bea-loading">
      <div class="bea-loading-title">{{ loading.label }}</div>
      <div class="bea-loading-bar">
        <div class="bea-loading-inner" :style="{ width: (loading.percent * 100).toFixed(1) + '%' }"></div>
      </div>
      <div class="bea-loading-percent">{{ (loading.percent * 100).toFixed(0) }}%</div>
    </div>

    <!-- 驾驶舱外框：顶栏 / 左侧建筑索引 / 右侧详情（含三维窗口）/ 底栏，四边均可收起。
         数据由组件内部按楼名拉「区域概览」接口（src/services/areaOverview.js）。
         ⚠️ 必须等场景真的建完再挂：加载期间先弹出一圈面板，等于在空底图上摆家具。
         pointer-events 只在面板自身开，地图交互不受影响。 -->
    <!-- 驾驶舱整体跑在 1920×1080 设计稿的 autofit 容器里：任意分辨率下等比缩放、
         四边贴满（三维画布在容器外，不受缩放影响；详情窗口的 scissor 对位按视觉
         矩形逐帧测量，缩放下天然对齐）。面板内部尺寸一律固定设计像素。 -->
    <AutoFit v-if="cockpitReady" id="ck-autofit">
      <CockpitFrame :buildings="buildings" :selected="selection" :selected-code="selectedCode"
        :view="anchor" :context-on="anchor.buildings" :labels-on="anchor.labels" :calib-open="calibOpen"
        :has-model="hasModel" @select="onCockpitSelect" @detail-slot="onDetailSlot" @reset-view="resetView"
        @orbit="onDetailOrbit" @orbit-start="onDetailOrbitStart" @orbit-end="onDetailOrbitEnd"
        @update:context="setContext" @update:labels="setLabels" @update:calib="calibOpen = $event"
        :watermark-on="watermarkOn" @update:watermark="watermarkOn = $event" />
    </AutoFit>

    <!-- 选中建筑的信息弹窗：贴着名称标牌右侧长出来，跟着镜头飘（点楼体或名称牌都弹）。
         与驾驶舱各栏互不影响：它只读 selection，关它也只关自己（选中态、左栏、三维转台照常）。
         定位走 scene.getSelectionScreen() 的每帧缓存，不经响应式，详见组件头注释。 -->
    <BuildingInfoPopup :selected="selection" :get-screen="getSelectionScreen" @close="closePopup" />

    <!-- 校准面板：核对模型与底图是否贴合，校完可折叠。
         注意 @pointerdown/@click/@wheel 的 .stop —— 面板是地图容器的子节点，
         不拦住的话按钮点击会冒泡到容器、被当作"点地图"（表现为点按钮时误选中建筑）。 -->
    <!-- 校准面板：由底栏"模型校准"按钮开关，浮在驾驶舱之上 -->
    <div v-if="panel.ready && calibOpen" class="bea-panel" :class="{ 'is-collapsed': panel.collapsed }"
      @pointerdown.stop @click.stop @wheel.stop @dblclick.stop>
      <div class="bea-panel-head" @click="panel.collapsed = !panel.collapsed">
        <span class="bea-panel-title">模型校准</span>
        <span class="bea-panel-fold">{{ panel.collapsed ? '▾' : '▴' }}</span>
      </div>

      <div v-show="!panel.collapsed" class="bea-panel-body">
        <div class="bea-row">
          <span class="bea-key">锚点 WGS84</span>
          <span class="bea-val">{{ fmt(state.wgs84.lat) }}, {{ fmt(state.wgs84.lon) }}</span>
        </div>
        <div class="bea-row">
          <span class="bea-key">地图中心 GCJ02</span>
          <span class="bea-val">{{ fmt(state.gcj[1]) }}, {{ fmt(state.gcj[0]) }}</span>
        </div>
        <div class="bea-row">
          <span class="bea-key">锚点来源</span>
          <span class="bea-val">{{ state.precise ? '模型真实几何（精确）' : 'tileset 元数据（估算）' }}</span>
        </div>

        <div class="bea-divider"></div>

        <div class="bea-row">
          <span class="bea-key">校准偏移</span>
          <span class="bea-val bea-strong">东 {{ state.calib.east }} m ｜ 北 {{ state.calib.north }} m</span>
        </div>
        <div class="bea-nudge">
          <span class="bea-nudge-label">东</span>
          <button class="bea-btn" @click="nudge(-20, 0)">-20</button>
          <button class="bea-btn" @click="nudge(-5, 0)">-5</button>
          <button class="bea-btn" @click="nudge(5, 0)">+5</button>
          <button class="bea-btn" @click="nudge(20, 0)">+20</button>
        </div>
        <div class="bea-nudge">
          <span class="bea-nudge-label">北</span>
          <button class="bea-btn" @click="nudge(0, -20)">-20</button>
          <button class="bea-btn" @click="nudge(0, -5)">-5</button>
          <button class="bea-btn" @click="nudge(0, 5)">+5</button>
          <button class="bea-btn" @click="nudge(0, 20)">+20</button>
        </div>
        <div class="bea-nudge">
          <button class="bea-btn bea-btn-wide" @click="resetCalib">归零（回到数据自洽位置）</button>
        </div>
        <div class="bea-nudge">
          <button class="bea-btn bea-btn-wide" @click="copyCalib">{{ panel.copied ? '已复制到剪贴板' : '复制校准值（写回代码用）'
            }}</button>
        </div>

        <!-- 单栋楼微调：点建筑模型（或点名称标牌）选中后可用 -->
        <template v-if="bEdit.key">
          <div class="bea-divider"></div>
          <div class="bea-sub">选中建筑微调 · {{ bEdit.name || bEdit.code }}</div>
          <div class="bea-row">
            <span class="bea-key">经纬度 WGS84</span>
            <span class="bea-val">{{ selection && selection.lla ? selection.lla.lat.toFixed(6) + ', ' +
              selection.lla.lon.toFixed(6) : '—' }}</span>
          </div>
          <div class="bea-row">
            <span class="bea-key">经纬度 GCJ02</span>
            <span class="bea-val">{{ selectedGCJ ? selectedGCJ[1].toFixed(6) + ', ' + selectedGCJ[0].toFixed(6) : '—'
              }}</span>
          </div>
          <div class="bea-row">
            <span class="bea-key">当前位置</span>
            <span class="bea-val">东 {{ bEdit.value.east.toFixed(1) }} ｜ 北 {{ bEdit.value.north.toFixed(1) }} ｜ 上
              {{ bEdit.value.up.toFixed(1) }}</span>
          </div>
          <div class="bea-nudge">
            <span class="bea-nudge-label">东</span>
            <button class="bea-btn" @click="nudgeBuilding('east', -10)">-10</button>
            <button class="bea-btn" @click="nudgeBuilding('east', -1)">-1</button>
            <button class="bea-btn" @click="nudgeBuilding('east', 1)">+1</button>
            <button class="bea-btn" @click="nudgeBuilding('east', 10)">+10</button>
          </div>
          <div class="bea-nudge">
            <span class="bea-nudge-label">北</span>
            <button class="bea-btn" @click="nudgeBuilding('north', -10)">-10</button>
            <button class="bea-btn" @click="nudgeBuilding('north', -1)">-1</button>
            <button class="bea-btn" @click="nudgeBuilding('north', 1)">+1</button>
            <button class="bea-btn" @click="nudgeBuilding('north', 10)">+10</button>
          </div>
          <div class="bea-nudge">
            <span class="bea-nudge-label">上</span>
            <button class="bea-btn" @click="nudgeBuilding('up', -10)">-10</button>
            <button class="bea-btn" @click="nudgeBuilding('up', -1)">-1</button>
            <button class="bea-btn" @click="nudgeBuilding('up', 1)">+1</button>
            <button class="bea-btn" @click="nudgeBuilding('up', 10)">+10</button>
          </div>
          <div class="bea-nudge">
            <span class="bea-nudge-label">缩放</span>
            <button class="bea-btn" @click="nudgeBuilding('scale', -0.01)">-0.01</button>
            <button class="bea-btn" @click="nudgeBuilding('scale', -0.001)">-0.001</button>
            <span class="bea-scale">{{ bEdit.value.scale.toFixed(3) }}</span>
            <button class="bea-btn" @click="nudgeBuilding('scale', 0.001)">+0.001</button>
            <button class="bea-btn" @click="nudgeBuilding('scale', 0.01)">+0.01</button>
          </div>
          <div class="bea-nudge">
            <label class="bea-check">
              <input type="checkbox" :checked="bEdit.value.visible !== false" @change="toggleBuildingVisible" />
              <span>显示该建筑（取消勾选即隐藏这栋楼）</span>
            </label>
          </div>
          <div class="bea-nudge">
            <button class="bea-btn bea-btn-wide" @click="resetBuilding">重置本栋楼</button>
            <button class="bea-btn bea-btn-wide" @click="copyBuildingTransforms">
              {{ bEdit.copied ? '已复制到剪贴板' : '复制微调值（写回代码用）' }}
            </button>
          </div>
        </template>

        <!-- 单模型微调：分片之间本来就有原点偏差，这里逐个分片挪位置 / 等比缩放 -->
        <template v-if="partEdit.list.length">
          <div class="bea-divider"></div>
          <div class="bea-sub">单模型微调（按分片）</div>
          <select v-model="partEdit.dir" class="bea-select" @change="syncPartValue">
            <option v-for="p in partEdit.list" :key="p.dir" :value="p.dir">{{ p.name }}</option>
          </select>
          <div class="bea-row">
            <span class="bea-key">当前位置</span>
            <span class="bea-val">东 {{ partEdit.value.east.toFixed(1) }} ｜ 北 {{ partEdit.value.north.toFixed(1) }} ｜ 上
              {{ partEdit.value.up.toFixed(1) }}</span>
          </div>
          <div class="bea-nudge">
            <span class="bea-nudge-label">东</span>
            <button class="bea-btn" @click="nudgePart('east', -10)">-10</button>
            <button class="bea-btn" @click="nudgePart('east', -1)">-1</button>
            <button class="bea-btn" @click="nudgePart('east', 1)">+1</button>
            <button class="bea-btn" @click="nudgePart('east', 10)">+10</button>
          </div>
          <div class="bea-nudge">
            <span class="bea-nudge-label">北</span>
            <button class="bea-btn" @click="nudgePart('north', -10)">-10</button>
            <button class="bea-btn" @click="nudgePart('north', -1)">-1</button>
            <button class="bea-btn" @click="nudgePart('north', 1)">+1</button>
            <button class="bea-btn" @click="nudgePart('north', 10)">+10</button>
          </div>
          <div class="bea-nudge">
            <span class="bea-nudge-label">上</span>
            <button class="bea-btn" @click="nudgePart('up', -10)">-10</button>
            <button class="bea-btn" @click="nudgePart('up', -1)">-1</button>
            <button class="bea-btn" @click="nudgePart('up', 1)">+1</button>
            <button class="bea-btn" @click="nudgePart('up', 10)">+10</button>
          </div>
          <div class="bea-nudge">
            <span class="bea-nudge-label">缩放</span>
            <button class="bea-btn" @click="nudgePart('scale', -0.01)">-0.01</button>
            <button class="bea-btn" @click="nudgePart('scale', -0.001)">-0.001</button>
            <span class="bea-scale">{{ partEdit.value.scale.toFixed(3) }}</span>
            <button class="bea-btn" @click="nudgePart('scale', 0.001)">+0.001</button>
            <button class="bea-btn" @click="nudgePart('scale', 0.01)">+0.01</button>
          </div>
          <div class="bea-nudge">
            <button class="bea-btn bea-btn-wide" @click="resetPart">重置本分片</button>
            <button class="bea-btn bea-btn-wide" @click="copyPartTransforms">
              {{ partEdit.copied ? '已复制到剪贴板' : '复制微调值（写回代码用）' }}
            </button>
          </div>
          <div class="bea-hint">缩放是以该分片自身中心为基准等比缩放，楼群不会整体跑偏。</div>
        </template>

        <div class="bea-divider"></div>

        <label class="bea-check">
          <input type="checkbox" :checked="state.buildings" @change="toggleBuildings($event)" />
          <span>显示高德自带 3D 楼块（模型占地范围内自动隐藏，可当校准参照）</span>
        </label>
        <label v-if="labelCount" class="bea-check">
          <input type="checkbox" :checked="state.labels" @change="toggleLabels($event)" />
          <span>显示建筑名称标牌（共 {{ labelCount }} 块，缩放太小时自动隐藏）</span>
        </label>
        <div class="bea-hint">
          偏移值已存入 localStorage，刷新后保留。移到正确位置后，把校准值写进
          <code>CALIBRATION</code> 常量即可固化。
        </div>
      </div>
    </div>

    <!-- 全屏水印：盖在三维画面与整个驾驶舱之上（.wm 的 pointer-events 已关，不吃交互）。
         开关在底栏右下角。放在 AutoFit 外面，字号才是真实屏幕像素、不随设计稿缩放。 -->
    <WatermarkOverlay v-if="watermarkOn" :text="watermarkText" />
  </div>
</template>

<script>
import { reactive, onMounted, onBeforeUnmount, ref, computed, nextTick } from 'vue'
import { loadCityTiles } from '@/three/loadCityTiles'
import { createModelScene, SHOW_CONTEXT_BUILDINGS } from '@/three/sceneView'
import { wgs2gcj } from '@/three/geo'
import CockpitFrame from './CockpitFrame.vue'
import AutoFit from './AutoFit.vue'
import WatermarkOverlay from './WatermarkOverlay.vue'
import BuildingInfoPopup from './BuildingInfoPopup.vue'

// 全屏水印文案。平铺单元按文字实际宽度算，长短都能铺满，改这一行就行。
const WATERMARK_TEXT = '海川智信'

// 人工校准偏移（米，正值让模型整体向东/向北移动）。
// 这里就是"固化值"：默认 东 80 / 北 20。面板里的临时微调只存在 localStorage，
// 一旦这里改了基准，旧的临时值会自动作废（见 readStoredCalibration）。
// const CALIBRATION = { east: 80, north: 20 }
const CALIBRATION = {  }

const LS_CALIB = 'fsg:calib'
const LS_BUILDINGS = 'fsg:showAmapBuildings'
// 高德楼块默认值（false = 不显示高德自带 3D 楼块）；同样用指纹作废旧偏好
const BUILDINGS_BASE_KEY = String(SHOW_CONTEXT_BUILDINGS)

// ===== 单模型微调（按分片）=====
// 固化值：key 是分片目录名（part.dir），值是 { east, north, up, scale }。
// east/north/up 单位米（正值向东/北/上），scale 是等比缩放倍数（以该分片自身中心为基准）。
// 例：{ '20260913part2': { east: -12.5, north: 8, up: 0, scale: 0.998 } }
const PART_TRANSFORMS = {
  "20260913_1/": { east: 129, north: -141, up: 0, scale: 1.27 },            // 1.尚城女装中心
  "20260913_2/": { east: -421, north: 245, up: 0, scale: 1.110 },          // 2.新九天女装大厦
  // "20260913_3/": { east: -278, north: -35, up: 0, scale: 1.0 },         // 3.苏杭首站
  // "20260913_4/": { east: 0, north: 0, up: 0, scale: 0 },                // 4.旺田·精品女装市场
  "20260913_5/": { east: 30, north: -23, up: 0, scale: 1.24 },               // 5.维众精品汇
  "20260913_6/": { east: 35, north: -14, up: 0, scale: 1.24 },              // 6.保太和饰品区
  "20260913_7/": { east: 499, north: 104, up: 0, scale: 1.07 },            // 7.新意法女装大厦
  "20260913_8/": { east: 620, north: 820, up: 0, scale: 1.16 },            // 8.杭州四季青面料市场
  "20260913_9/": { east: 165, north: 247, up: 0, scale: 1.00 },            // 9.佳宝原创设计师女装
  "20260913_10/": { east: 43, north: -10, up: 0, scale: 1.53 },             // 10.小白楼
  "20260913_11/": { east: 46, north: 30, up: 0, scale: 1.06 },            // 11.意法清江服装城
  "20260913_12/": { east: 94, north: -72, up: 0, scale: 1.02 },            // 12.中洲女装城
  '20260913_13/': { east: -515, north: 243, up: 0, scale: 1.02 },          // 13.四季青服装市场
  '20260913_14/': { east: -452, north: 254, up: 0, scale: 1.26 },          // 14.中纺中心服装城
  '20260913_15/': { east: 131, north: 8, up: 0, scale: 1.00 },            // 15.杭州意法服饰城
  "20260913_16/": { east: -520, north: 254, up: 0, scale: 1.16 },          // 16.新中洲女装城
  "20260913_17/": { east: 31, north: -27, up: 0, scale: 1.21 },              // 17.杭派精品服装市场
  '20260913_18/': { east: -272, north: -137, up: 0, scale: 1.0 },          // 18.爱尚潮品汇
  '20260913_19/': { east: -298, north: -165, up: 0, scale: 1.0 },          // 19.T9青年引力场
  '20260913_20/': { east: -416, north: 264, up: 0, scale: 1.04 },          // 20.昆龙童装城
  '20260913_21/': { east: -429, north: 266, up: 0, scale: 1.19 },          // 21.四季青精品童装市场
  '20260913_22/': { east: -36, north: -44, up: 0, scale: 1.02 },           // 22.常青休闲女装市场
  '20260913_23/': { east: 12, north: -52, up: 0, scale: 1.02 },            // 23.杭泵精品男装市场
}

const LS_PART = 'fsg:partTransforms'
const PART_BASE_KEY = JSON.stringify(PART_TRANSFORMS)

function readStoredPartTransforms() {
  try {
    const raw = localStorage.getItem(LS_PART)
    if (raw) {
      const v = JSON.parse(raw)
      if (v && v.base === PART_BASE_KEY && v.map && typeof v.map === 'object') return v.map
    }
  } catch (e) {
    /* 忽略损坏的本地缓存 */
  }
  return null
}

/** 固化值 + 本地临时微调合并：本地优先 */
function initialPartTransforms() {
  const stored = readStoredPartTransforms()
  return Object.assign({}, PART_TRANSFORMS, stored || {})
}

// ===== 单栋楼微调 =====
// key = `${分片目录}|${楼号}`，例如 '20260913part1/|13'
// 例：{ '20260913part1/|13': { east: -6.5, north: 3, up: 0, scale: 1.01 } }
/**
 * 9 号楼（佳宝原创设计师女装）往西北挪 22m：OSM 的新开河中心线离它的标牌锚点只有
 * 8.0m，而河宽 26m —— 等于河从楼底下穿过去（水面上看不见，是因为河在 z≈0.35，
 * 被 34m 高的楼体挡在里面，但环境层的避让、桥、楼前铺装全都算错）。
 * 模型位置是 ECEF→WGS84 解出来的，与 OSM 之间本来就有几十米的对齐残差（实测
 * 最近邻 19~57m、平均偏移 (-5,-17)m），22m 在这个误差范围内，比让河穿楼合理。
 * 其余三栋临河的楼量出来离中心线 25~30m（离水边 12~17m），是正常的"街边/河边楼"，不动。
 */
const BUILDING_TRANSFORMS = {
  '20260913_9/|9': { east: -21.2, north: 5.9, up: 0, scale: 1, visible: true },
}

const LS_BUILDING_T = 'fsg:buildingTransforms'
const BUILDING_BASE_KEY = JSON.stringify(BUILDING_TRANSFORMS)

function readStoredBuildingTransforms() {
  try {
    const raw = localStorage.getItem(LS_BUILDING_T)
    if (raw) {
      const v = JSON.parse(raw)
      if (v && v.base === BUILDING_BASE_KEY && v.map && typeof v.map === 'object') return v.map
    }
  } catch (e) {
    /* 忽略损坏的本地缓存 */
  }
  return null
}

function initialBuildingTransforms() {
  const stored = readStoredBuildingTransforms()
  return Object.assign({}, BUILDING_TRANSFORMS, stored || {})
}
// 固化值的指纹：用它判断 localStorage 里的临时偏移是不是基于当前基准调出来的
const CALIB_BASE_KEY = `${CALIBRATION.east},${CALIBRATION.north}`

function readStoredCalibration() {
  try {
    const raw = localStorage.getItem(LS_CALIB)
    if (!raw) return null
    const v = JSON.parse(raw)
    if (v && v.base === CALIB_BASE_KEY && Number.isFinite(v.east) && Number.isFinite(v.north)) {
      return { east: v.east, north: v.north }
    }
  } catch (e) {
    /* 忽略损坏的本地缓存 */
  }
  return null
}

function readStoredBuildings() {
  try {
    const raw = localStorage.getItem(LS_BUILDINGS)
    if (raw) {
      const v = JSON.parse(raw)
      if (v && v.base === BUILDINGS_BASE_KEY && typeof v.value === 'boolean') return v.value
    }
  } catch (e) {
    /* 忽略 */
  }
  return SHOW_CONTEXT_BUILDINGS
}

/**
 * 建筑效果A：自建 three.js 场景（src/three/sceneView.js）+ 23 栋编号楼模型 + OSM 环境层。
 * 交互只有旋转和缩放：注视点固定在 23 栋楼地面锚点的质心上，平移被关掉（见 sceneView
 * 的 controls.enablePan），所以园区不会被拖出画面。滚轮/中键推拉、左键或右键绕心旋转、
 * Shift+左键调俯仰。
 * 模型位置 = 其真实地理位置（ECEF 解算 -> WGS84），面板里的 WGS84/GCJ02 两行只做显示。
 */
export default {
  name: 'BuildingsEffectA',
  components: { CockpitFrame, AutoFit, WatermarkOverlay, BuildingInfoPopup },
  setup() {
    const containerRef = ref(null)
    const loading = reactive({ visible: true, percent: 0, label: '加载高德地图…' })
    // 校准面板默认收起，只留标题条，点标题条展开
    const panel = reactive({ ready: false, collapsed: true, copied: false })
    const anchor = reactive({
      wgs84: { lat: 0, lon: 0 },
      gcj: [0, 0],
      calib: { east: 0, north: 0 },
      precise: false,
      buildings: SHOW_CONTEXT_BUILDINGS,
      labels: true,
    })
    const labelCount = ref(0)
    // 当前选中的建筑（点标牌触发），null = 未选中
    const selection = ref(null)

    let scene = null
    const state = computed(() => anchor)

    // ---- 单模型微调 ----
    const partEdit = reactive({
      list: [],
      dir: '',
      value: { east: 0, north: 0, up: 0, scale: 1 },
      copied: false,
    })
    let partsInited = false

    // ---- 单栋楼微调（选中建筑后可用）----
    const bEdit = reactive({
      key: '',
      code: '',
      name: '',
      value: { east: 0, north: 0, up: 0, scale: 1, visible: true },
      copied: false,
    })

    function syncBuildingValue() {
      if (!scene || !bEdit.key) return
      const t = scene.getBuildingTransform(bEdit.key)
      if (t) Object.assign(bEdit.value, t)
    }

    function persistBuildings() {
      if (!scene) return
      const map = {}
      scene.getAllBuildingTransforms().forEach((b) => {
        if (b.east || b.north || b.up || b.scale !== 1 || b.visible === false) {
          map[b.key] = { east: b.east, north: b.north, up: b.up, scale: b.scale, visible: b.visible !== false }
        }
      })
      try {
        localStorage.setItem(LS_BUILDING_T, JSON.stringify({ base: BUILDING_BASE_KEY, map }))
      } catch (e) {
        /* 忽略 */
      }
    }

    /** 单栋楼显示/隐藏 */
    function toggleBuildingVisible(evt) {
      if (!scene || !bEdit.key) return
      const show = evt.target.checked
      scene.setBuildingTransform(bEdit.key, { visible: show })
      syncBuildingValue()
      persistBuildings()
    }

    function nudgeBuilding(field, delta) {
      if (!scene || !bEdit.key) return
      const cur = scene.getBuildingTransform(bEdit.key) || {}
      const base = cur[field] !== undefined ? cur[field] : bEdit.value[field] || 0
      const next = Number((base + delta).toFixed(4))
      scene.setBuildingTransform(bEdit.key, { [field]: next })
      syncBuildingValue()
      bEdit.copied = false
      persistBuildings()
    }

    function resetBuilding() {
      if (!scene || !bEdit.key) return
      scene.resetBuildingTransform(bEdit.key)
      syncBuildingValue()
      bEdit.copied = false
      persistBuildings()
    }

    function copyBuildingTransforms() {
      if (!scene) return
      const map = {}
      scene.getAllBuildingTransforms().forEach((b) => {
        if (b.east || b.north || b.up || b.scale !== 1 || b.visible === false) {
          map[b.key] = {
            east: Number(b.east.toFixed(2)),
            north: Number(b.north.toFixed(2)),
            up: Number(b.up.toFixed(2)),
            scale: Number(b.scale.toFixed(5)),
            visible: b.visible !== false,
          }
        }
      })
      const text = `const BUILDING_TRANSFORMS = ${JSON.stringify(map, null, 2)}`
      try {
        navigator.clipboard.writeText(text)
      } catch (e) {
        window.prompt('复制下面的内容写回代码：', text)
      }
      bEdit.copied = true
    }

    /** 选中建筑变化时，把面板切到那栋楼 */
    function syncBuildingEdit(info) {
      if (!info) return
      const key = `${info.part}|${info.code}`
      if (key === bEdit.key) return
      bEdit.key = key
      bEdit.code = info.code
      bEdit.name = info.name
      bEdit.copied = false
      Object.assign(bEdit.value, { east: 0, north: 0, up: 0, scale: 1 })
      syncBuildingValue()
    }

    function syncPartValue() {
      if (!scene || !partEdit.dir) return
      const t = scene.getPartTransform(partEdit.dir)
      if (t) Object.assign(partEdit.value, t)
    }

    function persistParts() {
      if (!scene || !partEdit.list.length) return
      const map = {}
      partEdit.list.forEach((p) => {
        const t = scene.getPartTransform(p.dir)
        if (t && (t.east || t.north || t.up || t.scale !== 1)) map[p.dir] = t
      })
      try {
        localStorage.setItem(LS_PART, JSON.stringify({ base: PART_BASE_KEY, map }))
      } catch (e) {
        /* 忽略 */
      }
    }

    function nudgePart(field, delta) {
      if (!scene || !partEdit.dir) return
      // 以场景里的当前值为基准做增减（不要用面板缓存值，否则外部改动后会错位）
      const cur = scene.getPartTransform(partEdit.dir) || {}
      const base = cur[field] !== undefined ? cur[field] : partEdit.value[field] || 0
      const next = Number((base + delta).toFixed(4))
      scene.setPartTransform(partEdit.dir, { [field]: next })
      syncPartValue()
      partEdit.copied = false
      persistParts()
    }

    function resetPart() {
      if (!scene || !partEdit.dir) return
      scene.resetPartTransform(partEdit.dir)
      syncPartValue()
      partEdit.copied = false
      persistParts()
    }

    function copyPartTransforms() {
      if (!scene) return
      const map = {}
      partEdit.list.forEach((p) => {
        const t = scene.getPartTransform(p.dir)
        if (t && (t.east || t.north || t.up || t.scale !== 1)) {
          map[p.dir] = {
            east: Number(t.east.toFixed(2)),
            north: Number(t.north.toFixed(2)),
            up: Number(t.up.toFixed(2)),
            scale: Number(t.scale.toFixed(5)),
          }
        }
      })
      const text = `const PART_TRANSFORMS = ${JSON.stringify(map, null, 2)}`
      try {
        navigator.clipboard.writeText(text)
      } catch (e) {
        window.prompt('复制下面的内容写回代码：', text)
      }
      partEdit.copied = true
    }

    /** 模型就绪后：拿到分片清单、同步本地存的微调值 */
    function initPartEdit() {
      if (!scene || partsInited) return
      const list = scene.listParts()
      if (!list.length) return
      partsInited = true
      partEdit.list = list
      partEdit.dir = list[0].dir
      syncPartValue()
    }

    // ---- 驾驶舱：选中态、详情三维视图、图层开关 ----
    /** 23 栋的编号 + 名称（来自标牌清单）；左侧索引与顶栏合计都吃它 */
    const buildings = ref([])
    /** 场景真正建好之前不挂驾驶舱：否则加载期间先摆一圈面板，底图还是空的 */
    const cockpitReady = ref(false)
    /** 面板里那块三维窗口的 DOM，由 CockpitFrame 通过 detail-slot 事件交回来 */
    let detailSlot = null
    const hasModel = ref(true)
    const calibOpen = ref(false)
    /** 全屏水印开关（底栏右下角那个按钮控制） */
    const watermarkOn = ref(true)

    const selectedCode = computed(() => (selection.value ? String(selection.value.code) : ''))

    // 选中建筑的 GCJ02 经纬度（校准面板要和 WGS84 并排显示，用来核对偏移）
    const selectedGCJ = computed(() => {
      const lla = selection.value && selection.value.lla
      if (!lla || !Number.isFinite(lla.lon) || !Number.isFinite(lla.lat)) return null
      return wgs2gcj(lla.lon, lla.lat) // [lon, lat]
    })

    /**
     * 把选中那栋楼交给场景：全息材质由 sceneView 的 selectSprite 负责，
     * 这里只管面板里那栋自转的模型 —— 槽位没挂载、或这栋楼没有独立单体模型
     * （3 号楼、4 号楼的几何还混在分片里）时，就把视图收掉，别留一个空窗口。
     */
    function syncDetail(code) {
      if (!scene) return
      if (!code || !detailSlot || !scene.hasBuildingModel(code)) {
        hasModel.value = false
        scene.hideBuildingDetail()
        return
      }
      hasModel.value = true
      scene.showBuildingDetail(code, detailSlot)
    }

    function onDetailSlot(el) {
      detailSlot = el || null
      syncDetail(selectedCode.value)
    }

    function onCockpitSelect(code) {
      if (scene) scene.selectByCode(code)
    }

    // 详情窗口里的拖拽：组件只报位移增量，角度与惯性在 buildingDetailView 里累加
    function onDetailOrbitStart() {
      if (scene) scene.beginDetailOrbit()
    }

    function onDetailOrbit(dx, dy) {
      if (scene) scene.orbitDetailBuilding(dx, dy)
    }

    function onDetailOrbitEnd() {
      if (scene) scene.endDetailOrbit()
    }

    function resetView() {
      if (scene) scene.resetView()
    }

    function setContext(show) {
      if (!scene) return
      scene.setContextVisible(show)
      anchor.buildings = !!show
      try {
        localStorage.setItem(LS_BUILDINGS, JSON.stringify({ value: !!show, base: BUILDINGS_BASE_KEY }))
      } catch (e) {
        /* 忽略损坏/不可用的 localStorage */
      }
    }

    function setLabels(show) {
      if (!scene) return
      scene.setLabelsVisible(show)
      anchor.labels = !!show
    }

    function handleSelect(info) {
      const prev = selection.value ? String(selection.value.code) : ''
      selection.value = info
      if (!info) {
        syncDetail('')
        return
      }
      syncBuildingEdit(info) // 校准面板跟着切到"选中建筑微调"
      // 换楼才重挂视图：槽位元素本身不变，得主动再喂一次
      if (String(info.code) !== prev) nextTick(() => syncDetail(String(info.code)))
    }

    /**
     * 贴牌弹窗每帧读一次的标牌屏幕位置（sceneView 里缓存的普通对象，非响应式）。
     * 走这条路而不是 selection.screen，是为了不把屏幕坐标塞进 Vue —— 见 sceneView
     * emitSelection 里"键里不能放屏幕坐标"那段注释，那正是"点建筑很卡"的一半成因。
     */
    function getSelectionScreen() {
      return scene && scene.getSelectionScreen ? scene.getSelectionScreen() : null
    }

    /** 弹窗 ×：只收弹窗。选中态由场景侧的 panel 标志管，左栏详情与三维转台不受影响 */
    function closePopup() {
      if (scene) scene.setPanelOpen(false)
    }

    const setProgress = (loaded, total, label) => {
      if (label) loading.label = label
      loading.percent = total ? Math.min(0.99, loaded / total) : loading.percent
    }

    async function init() {
      try {
        // 纯 three 场景：自建相机与渲染循环，不再需要经纬度锚点（模型布局是局部米制 ENU）
        scene = await createModelScene({
          container: containerRef.value,
          partTransforms: initialPartTransforms(),
          buildingTransforms: initialBuildingTransforms(),
          showContextBuildings: readStoredBuildings(),
          calibration: readStoredCalibration() || CALIBRATION,
          onViewChange: (info) => {
            Object.assign(anchor, info)
            panel.ready = true
            if (info.precise) initPartEdit() // 模型就绪 -> 拿到分片清单
          },
          onSelect: (info) => handleSelect(info),
          loadModel: async () => {
            loading.visible = true
            loading.label = '加载建筑模型'
            const city = await loadCityTiles(setProgress)
            loading.visible = false
            // 只有 scenetree 里带了业务 name、且确实解析到位置的建筑才会有标牌；
            // 没有对应名称的一律不生成（周边白模的 Mesh_N 就是这样被过滤掉的）
            labelCount.value = city.labelAnchors ? city.labelAnchors.length : 0
            if (!labelCount.value && scene) scene.setLabelsVisible(false)
            // 左侧索引的清单直接取自标牌：只有 scenetree 里带业务名、且真解析到位置的楼才有牌子
            buildings.value = (city.labelAnchors || [])
              .map((a) => ({ code: String(a.code), name: a.name }))
              .sort((x, y) => (Number(x.code) || 0) - (Number(y.code) || 0))
            return city
          },
        })
        console.info(
          `[BuildingsEffectA] 场景已就绪｜zoom ${scene.getZoom().toFixed(2)}｜俯仰 ${scene.getPitch().toFixed(0)}°` +
          `｜周边城市: ${readStoredBuildings() ? '显示' : '隐藏'}`,
        )
        // 调试句柄：控制台可直接调 scene.setCalibration / setContextVisible / getState
        if (process.env.NODE_ENV !== 'production') window.__scene = scene
        // 场景（模型 + 环境层 + 标牌）全部就位后才让驾驶舱进场
        cockpitReady.value = true
      } catch (e) {
        console.error('[BuildingsEffectA] 场景初始化失败:', e)
        loading.label = '初始化失败：' + (e && e.message ? e.message : '未知错误')
      }
    }

    function nudge(dEast, dNorth) {
      if (!scene) return
      const cur = scene.getCalibration()
      const next = { east: cur.east + dEast, north: cur.north + dNorth }
      scene.setCalibration(next)
      persist(next)
    }

    function resetCalib() {
      if (!scene) return
      const next = { east: 0, north: 0 }
      scene.setCalibration(next)
      persist(next)
    }

    function persist(next) {
      try {
        localStorage.setItem(LS_CALIB, JSON.stringify({ ...next, base: CALIB_BASE_KEY }))
      } catch (e) {
        /* 忽略 */
      }
      panel.copied = false
    }

    function toggleBuildings(evt) {
      if (!scene) return
      const show = evt.target.checked
      scene.setBuildingsVisible(show)
      try {
        localStorage.setItem(LS_BUILDINGS, JSON.stringify({ value: show, base: BUILDINGS_BASE_KEY }))
      } catch (e) {
        /* 忽略 */
      }
    }

    function toggleLabels(evt) {
      if (!scene) return
      scene.setLabelsVisible(evt.target.checked)
    }

    function copyCalib() {
      const text =
        `// 粘贴到 src/components/BuildingsEffectA.vue 的 CALIBRATION 常量\n` +
        `const CALIBRATION = { east: ${anchor.calib.east}, north: ${anchor.calib.north} }\n`
      const done = () => {
        panel.copied = true
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, done)
      } else {
        done()
      }
      console.info(text)
    }

    const fmt = (v) => (Number.isFinite(v) ? v.toFixed(7) : '—')

    onMounted(() => {
      init()
    })

    onBeforeUnmount(() => {
      if (scene) {
        scene.dispose()
        scene = null
      }
    })

    return {
      containerRef,
      loading,
      panel,
      anchor,
      state,
      labelCount,
      selection,
      // 贴牌信息弹窗
      getSelectionScreen,
      closePopup,
      // 驾驶舱
      cockpitReady,
      buildings,
      selectedCode,
      hasModel,
      calibOpen,
      watermarkOn,
      watermarkText: WATERMARK_TEXT,
      onCockpitSelect,
      onDetailSlot,
      onDetailOrbitStart,
      onDetailOrbit,
      onDetailOrbitEnd,
      resetView,
      setContext,
      setLabels,
      // 校准面板
      partEdit,
      syncPartValue,
      nudgePart,
      resetPart,
      copyPartTransforms,
      bEdit,
      nudgeBuilding,
      resetBuilding,
      toggleBuildingVisible,
      copyBuildingTransforms,
      selectedGCJ,
      nudge,
      resetCalib,
      toggleBuildings,
      toggleLabels,
      copyCalib,
      fmt,
    }
  },
}
</script>

<style scoped>
.bea-container {
  position: absolute;
  inset: 0;
  overflow: hidden;
  /* 原来是浅蓝 #a9c5e0（高德底图加载前的过渡色）。现在没有底图了，
     首帧之前闪一块浅蓝很突兀，直接给夜色，和渲染器的 clear color 对齐 */
  background: #070d18;
}

.bea-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: #a9c5e0;
  color: #1f4e79;
  font-family: 'Microsoft YaHei', sans-serif;
  z-index: 10;
}

.bea-loading-title {
  font-size: 15px;
  letter-spacing: 2px;
}

.bea-loading-bar {
  width: 320px;
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.6);
  overflow: hidden;
}

.bea-loading-inner {
  height: 100%;
  background: linear-gradient(90deg, #1668dc, #22d3ee);
  transition: width 0.2s ease;
}

.bea-loading-percent {
  font-size: 13px;
  color: #3a6b96;
}

/* ---- 校准面板：调试用浮层，由底栏"模型校准"按钮开关 ---- */
.bea-panel {
  position: absolute;
  left: 496px; /* 让开左侧建筑索引（驾驶舱经 autofit 缩放，此面板不缩放，取近似值） */
  bottom: 58px; /* 浮在底栏上方 */
  width: 320px;
  max-height: calc(100% - 150px);
  overflow: auto;
  z-index: 200; /* 盖在驾驶舱的 autofit 容器（z-index:100）之上 */
  box-sizing: border-box;
  border-radius: 16px;
  background: linear-gradient(160deg, #ffffff 0%, #f3f6fa 100%);
  border: 1px solid rgba(160, 180, 200, 0.35);
  box-shadow: 0 22px 44px rgba(15, 45, 80, 0.22), 0 4px 12px rgba(15, 45, 80, 0.12),
    inset 0 1px 0 #ffffff;
  font-family: 'Microsoft YaHei', sans-serif;
  color: #2b3440;
}

.bea-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px 10px;
  cursor: pointer;
}

.bea-panel-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #2b3440;
}

.bea-panel-fold {
  width: 22px;
  height: 22px;
  line-height: 22px;
  text-align: center;
  font-size: 12px;
  color: #7a8490;
  background: #e8eef4;
  border-radius: 50%;
}

.bea-panel-body {
  padding: 0 18px 16px;
}

.bea-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  line-height: 1.95;
}

.bea-key {
  font-size: 12px;
  color: #8a94a0;
  white-space: nowrap;
}

.bea-val {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: #2b3440;
  text-align: right;
  word-break: break-all;
}

.bea-val.bea-strong {
  color: #2f9e63;
  font-weight: 700;
}

.bea-divider {
  height: 1px;
  margin: 10px 0;
  background: rgba(140, 165, 190, 0.3);
}

.bea-nudge {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

.bea-nudge-label {
  width: 18px;
  font-size: 12px;
  color: #8a94a0;
}

.bea-sub {
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #2b3440;
}

.bea-select {
  width: 100%;
  margin-bottom: 4px;
  padding: 5px 8px;
  font-size: 12px;
  font-family: inherit;
  color: #2b3440;
  background: #f4f7fb;
  border: 1px solid rgba(140, 165, 190, 0.45);
  border-radius: 8px;
  cursor: pointer;
}

.bea-select:focus {
  outline: none;
  border-color: #7d95ae;
}

.bea-scale {
  min-width: 48px;
  text-align: center;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: #2b3440;
}

.bea-btn {
  flex: 1;
  padding: 6px 0;
  font-size: 12px;
  font-family: inherit;
  color: #5b6672;
  background: #e8eef4;
  border: 0;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.bea-btn:hover {
  background: #dbe5ee;
}

.bea-btn:active {
  background: #cfdbe6;
}

.bea-btn-wide {
  flex: none;
  width: 100%;
}

.bea-check {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.6;
  color: #2b3440;
  cursor: pointer;
}

.bea-check input {
  margin-top: 2px;
  accent-color: #3dd68c;
}

.bea-hint {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(140, 165, 190, 0.3);
  font-size: 10px;
  line-height: 1.7;
  color: #a3b0be;
}

.bea-hint code {
  padding: 0 3px;
  background: #f4f7fb;
  border-radius: 3px;
  color: #4a86c8;
}
</style>
